import { Alert, Boundary, Button, Collapse, Icon, Menu, MenuItem, OverflowList, Overlay, Popover, Position, ResizeSensor, Tooltip } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { default as cx } from "classnames";
import React, { ReactNode, useState } from "react";
import styled, { css } from "styled-components";

import Label from "core/components/label/Label";
import { IViewProps, View } from "core/decorators/View";
import IconView from "../icon/Icon.view";
import { RowsView } from "../rows/Rows.view";
import { MenuTreeViewConfig } from "./MenuTreeViewConfig";
import { FontAwesomeIconView } from "../fontAwesomeIcon/FontAwesomeIcon.view";

/**
 * responsive menu tree view
 *
 * @author chills
 */

export interface IMenuNode<T = any> {
	id: string;
	label: JSX.Element | string;
	icon?: JSX.Element | string;
	className?: string;
	__type?: string;
	children?: Array<IMenuNode<T>> | (() => React.ReactNode);
	data?: T;
	attrs?: object; // data attributes for link DOM node
	isActive?: boolean;
	isDisabled?: boolean;
	isExpanded?: boolean;
	isSelectable?: boolean; // = true. if true, submenu expand toggle will be rendered as a separate button.
	isVisible?: boolean; // if false, link will not be rendered
	onSelect?: (node: IMenuNode<T>, index: number, arr: IMenuNode[], depth: number) => void; // override the tree's on select for a particular item.
}

interface IMenuTreeViewProps<T = any> extends IViewProps<MenuTreeViewConfig> {

	items: Array<IMenuNode<T>>;
	onSelectItem: (item: IMenuNode<T>, index: number, arr: Array<IMenuNode<T>>, depth: number) => void;
	onExpandedStateChange: (item: IMenuNode<T>, nextState: boolean) => void;
	isItemExpanded?: (item: IMenuNode<T>, index: number, arr: Array<IMenuNode<T>>, depth: number) => boolean;

	// default true. if true, selecting a popover menu item will close the popover.
	// not putting this in the config because probably depends a lot on the usage?
	dismissOnSelect?: boolean;
}

interface IStyledMenuProps {
	config: MenuTreeViewConfig;
}

const StyledMenu = styled.div<IStyledMenuProps>(({config}) => [
	!config.useRowsView && css`
		display: flex;
		flex-direction: ${config.vertical ? 'column' : 'row'};
		flex-wrap: ${config.useOverflowList ? 'nowrap' : 'wrap'};
		justify-content: ${config.justifyContent};
	`,
	css`
		background-color: ${config.background};
		color: ${config.color};
		padding: ${config.padding};

		& a {
			color: inherit;
		}

		& .__accordion-header,
		& .__accordion-header.__menu-item {
			display: flex;
			justify-content: space-between;
		}

		& .__accordion-header .__name {
			flex-grow: 1;
		}

		& .__icon {
			margin-right: ${config.iconPadding};
		}

		& .__menu-label {
			display: flex;
		}

		& .__menu-item {
			color: inherit;
			background-color: ${config.linkBackgroundColor};
			padding: ${config.linkPadding};
			margin: ${config.linkMargin};
			box-shadow: ${config.boxShadow};
			position: relative;
			box-sizing:  border-box;
			display: flex;
			align-items: center;
			font-size: ${config.fontSize};
			font-weight: ${config.fontWeight};
			text-transform: ${config.textTransform};
			justify-content: ${config.justifyContent};


			${config.minHeight && css`
				min-height: ${config.minHeight};
			`}
			
			${config.border && config.borderBottom && css`
				border-bottom: ${config.border};
				
				&:first-child {
					border-top: ${config.border};
				}
			`}
			
			${config.border && config.borderRight && css`
				border-bottom: ${config.border};
				
				&:first-child {
					border-left: ${config.border};
				}
			`}
			
			
		}

		& .__menu-item:hover {
			background-color: ${config.hoverBackground};
			color: ${config.hoverColor};
		}

		& .__menu-item.active,
		& .__menu-item:active {
			background-color: ${config.activeBackground};
			color: ${config.activeColor};
		}

		& .__menu-item.active::before {
			content: "";
			display: block;
			position: absolute;
			background-color: ${config.activeHighlightColor};
		}

		& .bp3-popover-wrapper {
			display: inherit;
		}

		& .collapse-link {
			display: flex;

			& > *:first-child {
				margin-right: 8px;
			}
		}
	`,
	// active highlight thing
	config.vertical ? css`
		& .__menu-item.active::before {
			bottom: 0;
			top: 0;
			right: 0;
			width: ${config.activeHighlightWidth};
		}
	` : css`
		& .__menu-item.active::before {
			bottom: 0;
			right: 0;
			left: 0;
			height: ${config.activeHighlightWidth};
		}
	`
]);

const CLASSES = {

	MENU_ITEM: "__menu-item",
	MENU_LABEL: "__menu-label",
	ICON: "__icon",
	EXPAND: "__expand",
	NAME: "__name",
	ACCORDION_HEADER: "__accordion-header",
	ACTIVE: "active"
};

export enum MenuTreeVariant {
	NAVBAR = "navbar",
	PILLS = "pills",
	TOOLBAR = "toolbar",
	ACTIONS = "actions",
	ACCORDION = "accordion"
}

@View({id: "menu-tree", configClass: MenuTreeViewConfig, variants: MenuTreeVariant})
export default class MenuTreeView<T> extends React.Component<IMenuTreeViewProps<T>, { collapsed: boolean, overlayOpen: boolean, isPopOverOpen: boolean }> {
	private container = React.createRef<HTMLDivElement>();
	public state = { collapsed: false, overlayOpen: false, isPopOverOpen: false };

	private onResize = () => {
		const { props: { config }, container: { current } } = this;

		if(config.collapseWidth && current) {

			// get the width of the menu container from reference
			const width = current.getBoundingClientRect().width;

			this.setState({ collapsed: config.collapseWidth > width });
		}
	}

	public render() {
		const { config, items } = this.props;

		if (!items || items.length < 1) {
			return null;
		}

		const menu = (
			<StyledMenu config={config} ref={this.container}>
				{this.renderMenu()}
			</StyledMenu>
		);

		if(config.collapseWidth) {
			return (
				<ResizeSensor onResize={this.onResize}>
					{menu}
				</ResizeSensor>
			);
		}

		return menu;
	}

	private renderMenu = () => {
		const { config, items = [], viewKey } = this.props;

		if(this.state.collapsed) {
			return this.renderOverflow(items, (
				<div className="collapse-link">
					<Icon icon={IconNames.MENU} />
					<Label k="menu.collapsed.toggle" v="Menu" />
				</div>
			), config.collapsedView);
		}

		if (config.vertical) {
			return items.map(this.renderNode);
		}

		if (config.useRowsView) {
			return (
				<RowsView viewKey={viewKey + '.rows'}>
					{items.map(this.renderNode)}
				</RowsView>
			);
		}

		if (config.useOverflowList) {
			return (
				<OverflowList
					collapseFrom={Boundary.END}
					items={items}
					overflowRenderer={this.renderOverflow}
					visibleItemRenderer={(item, index) => this.renderNode(item, index, items)}
				/>
			);
		}

		return items.map(this.renderNode);
	}

	private renderOverflow = (overflowItems: Array<IMenuNode<T>>, icon?: ReactNode, view?: string) => {
		const { config } = this.props;

		if("overlay" === view) {
			return (
				<React.Fragment>
					<a className={CLASSES.MENU_ITEM} onClick={() => this.setState({ overlayOpen: true })} >
						{icon || <Icon icon={IconNames.MORE} />}
					</a>
					<Overlay
						isOpen={this.state.overlayOpen}
						onClose={() => this.setState({overlayOpen: false})}
					>
						<div className="bp3-card bp3-elevation-4 bp3-overlay-content bp3-overlay-enter-done menu-overlay-position">
							<Menu>
								{overflowItems.map((item, index, arr) => this.renderNode(item, index, arr, 1))}
							</Menu>
						</div>
					</Overlay>
				</React.Fragment>
			);
		}

		return (
			<Popover
				autoFocus={false}
				content={
					<Menu>
						{overflowItems.map((item, index, arr) => this.renderNode(item, index, arr, 1))}
					</Menu>
				}
				minimal={config.popoverMinimal}
				position={Position.BOTTOM}
				isOpen={this.state.isPopOverOpen}
			>
				<a className={CLASSES.MENU_ITEM} tabIndex={0}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							this.setState({ isPopOverOpen: !this.state.isPopOverOpen })
						}
					}}
					onClick={(e) => {
						this.setState({ isPopOverOpen: !this.state.isPopOverOpen })

					}}>
					{icon || <Icon icon={IconNames.MORE} />}
				</a>
			</Popover>
		);
	}

	private renderNode = (item: IMenuNode<T>, index: number, arr: Array<IMenuNode<T>>, depth: number = 0): JSX.Element | null => {

		if (item.isVisible === false) {
			return null;
		}

		if (item.__type && item.__type === "container" && item.children && !item.children.length) {
			return null;
		}

		const { config, dismissOnSelect = true, onExpandedStateChange, onSelectItem } = this.props;

		// 1. popover menu item
		if (depth > 0 && !config.accordion) {
			return (
				<MenuItem
					{...item.attrs || {}}
					key={item.id}
					active={item.isActive}
					disabled={item.isDisabled}
					text={item.label}
					icon={config.enableIcons ? (item.icon as any) : ''}
					shouldDismissPopover={dismissOnSelect}
					onClick={() => this.handleSelect(item, index, arr, depth)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								this.handleSelect(item, index, arr, depth)
							}
						}}
					className={item.className}
						tabIndex={0}
				>
					{this.renderChildren(item.children, depth)}
				</MenuItem>
			);
		}


		// the link label-ish content
		let content = (
			<div className={[CLASSES.MENU_LABEL, item.className].join(" ")} onKeyDown={(e) => {
				if (e.key === 'Enter') {
					this.handleSelect(item, index, arr, depth)
				}
			}}>
				{config.enableIcons ? ( // icon can be a element here too. if the link type provides its own icon implementation and element, IconView will return that directly;
					typeof item.icon === 'string' || !item.icon? (
						<IconView viewKey={this.props.viewKey + ".icon"} icon={item.icon}>
							{config.enableLabels && item.label}
						</IconView>
					) : (
						<FontAwesomeIconView viewKey="fontAwesome icon" style={{position: 'absolute'}}>
							<i className={item && item.icon ? item.icon.props.icon : ''}/>
							{config.enableLabels && item.label}
						</FontAwesomeIconView>
					)
				) : (
					config.enableLabels && item.label
				)}
			</div>
		);

		if (config.enableTooltips) {
			content = (
				<Tooltip content={item.label}>
					{content}
				</Tooltip>
			);
		}

		const selectable: boolean = item.isSelectable !== false;
		const expanded: boolean = !!item.isExpanded;
		const hasChildren = !!item.children && item.children.length > 0;

		const expandIcon = <Icon icon={expanded ? IconNames.CHEVRON_DOWN : IconNames.CHEVRON_LEFT} />;

		// accordion
		if (config.accordion) {

			return (
				<div key={item.id}>
					{
						(selectable && hasChildren) ? (
							<div className={cx(CLASSES.MENU_ITEM, CLASSES.ACCORDION_HEADER, item.isActive && CLASSES.ACTIVE)}>
								<a {...item.attrs || {}} className={CLASSES.NAME} onClick={() => this.handleSelect(item, index, arr, depth)}>
									{content}
								</a>
								{
									(depth >= (config.autoExpand || 0)) && (
										<a className={CLASSES.EXPAND} onClick={() => onExpandedStateChange(item, !expanded)}>
											{expandIcon}
										</a>
									)
								}
							</div>
						) : (
							<a
								className={cx(CLASSES.MENU_ITEM, CLASSES.ACCORDION_HEADER, item.isActive && CLASSES.ACTIVE)}
								onClick={() => {
									if (hasChildren && !selectable) {
										onExpandedStateChange(item, !expanded);
									}
									if (!hasChildren && selectable) {
										this.handleSelect(item, index, arr, depth);
									}
								}}
							>
								<span className={CLASSES.NAME}>
									{content}
								</span>
								{
									(depth >= (config.autoExpand || 0)) && hasChildren && (
										<span className={CLASSES.EXPAND}>
											{expandIcon}
										</span>
									)
								}
							</a>
						)
					}
					{
						hasChildren && (
							<Collapse isOpen={expanded || (depth < (config.autoExpand || 0))}>
								<div style={{paddingLeft: '10px'}}>
									{this.renderChildren(item.children, depth)}
								</div>
							</Collapse>
						)
					}
				</div>
			);
		}

		if (item.children && (typeof item.children === 'function' || item.children.length > 0)) {

			const popoverProps = {
				content: (
					<Menu>
						{this.renderChildren(item.children, depth)}
					</Menu>
				),
				autoFocus: false,
				minimal: config.popoverMinimal,
				position: Position.BOTTOM,
				isOpen: expanded,
				onInteraction: (state: boolean) => onExpandedStateChange(item, state)
			};

			// if item  isn't selectable, use it
			if (!selectable) {
				return (
					<Popover key={item.id} {...popoverProps}>
						<div style={{ cursor: "pointer" }} className={cx(CLASSES.MENU_ITEM, item.isActive && CLASSES.ACTIVE)} onKeyDown={(e) => {
							if (e.key === 'Enter') {
								onExpandedStateChange(item, !expanded);
							}
						}}>
							<Icon icon={IconNames.CHEVRON_DOWN} />
							{content}
						</div>
					</Popover>

				);
			}

			return (
				<span key={item.id} style={{display: "flex"}}>
					<a {...item.attrs as any || {}} className={cx(CLASSES.MENU_ITEM, item.isActive && CLASSES.ACTIVE)} onClick={() => this.handleSelect(item, index, arr, depth)}>
						{content}
					</a>
					<Popover {...popoverProps}>
						<div className={CLASSES.EXPAND} onKeyDown={(e) => {
							if (e.key === 'Enter') {
								onExpandedStateChange(item, !expanded);
							}
						}}>
							<Icon icon={expandIcon} />
						</div>
					</Popover>
				</span>
			);
		}

		return (
			<a {...item.attrs || {}} key={item.id} className={cx(CLASSES.MENU_ITEM, item.isActive && CLASSES.ACTIVE)} onClick={() => this.handleSelect(item, index, arr, depth)}>
				{content}
			</a>
		);
	}

	private renderChildren = (children: Array<IMenuNode<T>> | (() => React.ReactNode), depth: number) => {
		if (typeof children === 'function') {
			return children();
		}

		if (Array.isArray(children) && children.length > 0) {
			return children.map((item, index, arr) => this.renderNode(item, index, arr, depth + 1));
		}

		return null;
	}

	private handleSelect = (item: IMenuNode<any>, index: number, arr: IMenuNode[], depth: number) => {
		if (typeof item.onSelect === 'function') {
			item.onSelect(item, index, arr, depth);
		} else {
			if (item && item.data && item.data.pagePath) {
            	localStorage.setItem("page-path", JSON.stringify(item.data));
            }
			this.props.onSelectItem(item, index, arr, depth);
		}
	}
}



// WEBPACK FOOTER //
// ./src/common/views/menuTree/MenuTree.view.tsx